import '@revelio/ui/fonts';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/source-sans-pro/700.css';

import FlagProvider, { type IConfig } from '@unleash/proxy-client-react';
import { authExchange } from '@urql/exchange-auth';
import get from 'lodash/get';
import inRange from 'lodash/inRange';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  cacheExchange,
  createClient,
  fetchExchange,
  Provider as UrqlProvider,
} from 'urql';

import '../index.css';

import App from './app/app';
import { getOrCreatePersistentUserId } from './app/utils/getOrCreatePersistentUserId';
import { environment } from './environment';
import { initTracking } from './tracking';

initTracking();

const sessionId = getOrCreatePersistentUserId();

const unleashedConfig: IConfig = {
  url: environment.PUBLIC_UNLEASH_URL,
  clientKey: environment.PUBLIC_UNLEASH_CLIENT_KEY,
  refreshInterval: 60,
  appName: 'reveliolabs-dashboard',
  context: {
    userId: sessionId,
  },
};

const urqlClient = createClient({
  url: `${environment.PUBLIC_GO_API_ROOT}/query`,
  preferGetMethod: false,
  fetchOptions: {
    credentials: 'include',
  },
  exchanges: [
    cacheExchange,
    authExchange(async ({ appendHeaders }) => {
      return {
        addAuthToOperation: (operation) => {
          return appendHeaders(operation, {});
        },
        willAuthError: () => {
          return false;
        },
        didAuthError: (error) => {
          const code = get(error, 'response.status', 200);
          const authError = inRange(code, 400, 500);
          if (authError && code == 404) {
            return false;
          }
          return authError;
        },
        refreshAuth: async () => {
          // No-op: a session can't be refreshed here without the user's password.
        },
      };
    }),
    fetchExchange,
  ],
});

const rootContainer = document.getElementById('root') as Element;
const root = createRoot(rootContainer);

root.render(
  <StrictMode>
    <UrqlProvider value={urqlClient}>
      <FlagProvider config={unleashedConfig}>
        <App />
      </FlagProvider>
    </UrqlProvider>
  </StrictMode>
);
